import { AdminGameSelector } from "@/components/classic-admin/AdminGameSelector";
import { Fragment, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Menu, X } from "lucide-react";
import { useAuthStore } from "@/lib/auth-store";
import { useDirtyStore } from "@/lib/dirty-store";
import { cn } from "@/lib/utils";
import { WorkspaceNavigation } from "./WorkspaceNavigation";
import "@/components/players/players-workspace.css";
import "./workspace-page.css";

const roleLabel = (role?: string) => role ? role[0].toUpperCase() + role.slice(1) : "";

export type Crumb = { label: string; to?: string };

/** True for admins whose role cannot change data (the server rejects their writes too). */
export function useRoleReadOnly() {
  const role = useAuthStore(s => s.me?.role);
  return !!role && role !== "internal";
}

/** True in the copied-data design preview, where every API write is rejected, or for a view-only role. */
export function usePreviewReadOnly() {
  const roleReadOnly = useRoleReadOnly();
  const [readOnly, setReadOnly] = useState(import.meta.env.VITE_DASHBOARD_PREVIEW === "true");
  useEffect(() => {
    if (import.meta.env.DEV || import.meta.env.VITE_DASHBOARD_PREVIEW === "true") void fetch("/__fixtures").then(async r => {
      if (r.ok && r.headers.get("content-type")?.includes("application/json")) { const body = await r.json() as { mode?: string; readOnly?: boolean }; setReadOnly(body.mode === "snapshot" || body.readOnly === true); }
    }).catch(() => {});
  }, []);
  return readOnly || roleReadOnly;
}

/**
 * Sidebar, phone navigation and top bar shared by soft-charcoal workspace pages.
 * Pages render their own scroll area (usually `.ws-scroll > .ws-page`) as children.
 */
export function WorkspaceShell({ crumbs, className, readOnly, children }: { crumbs: Crumb[]; className?: string; readOnly?: boolean; children: React.ReactNode }) {
  const navDialog = useRef<HTMLDialogElement>(null);
  const me = useAuthStore(s => s.me);
  const roleReadOnly = useRoleReadOnly();
  function leave(event: React.MouseEvent) { if (!useDirtyStore.getState().confirmLeave()) event.preventDefault(); }
  return <div className={cn("players-workspace ws-workspace", className)}>
    <aside className="pw-sidebar"><WorkspaceNavigation /></aside>
    <dialog ref={navDialog} className="pw-mobile-nav" aria-label="Main navigation">
      <button className="pw-icon-button pw-nav-close" aria-label="Close navigation" onClick={() => navDialog.current?.close()}><X size={20} /></button>
      <WorkspaceNavigation onNavigate={() => navDialog.current?.close()} />
    </dialog>
    <main className="pw-main">
      <header className="pw-topbar">
        <button className="pw-icon-button pw-menu-button" aria-label="Open navigation" onClick={() => navDialog.current?.showModal()}><Menu size={21} /></button>
        <AdminGameSelector className="pw-topbar-brand" />
        <span className="pw-breadcrumb">{crumbs.map((crumb, index) => <Fragment key={index}>{index > 0 && <ChevronRight size={13} />}
          {index === crumbs.length - 1 ? <strong>{crumb.label}</strong> : crumb.to ? <Link to={crumb.to} onClick={leave}>{crumb.label}</Link> : <span>{crumb.label}</span>}</Fragment>)}</span>
        <div className="pw-topbar-right">{readOnly && <span className="pw-preview-label"><span />{roleReadOnly ? `${roleLabel(me?.role)} · View only` : "Preview · Read only"}</span>}</div>
      </header>
      {children}
    </main>
  </div>;
}
